import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { io as socketIO } from 'socket.io-client'
import { X, Trophy, ArrowLeft, Copy, Play, Loader2, Check, Circle } from 'lucide-react'
import { mapvetoAPI } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import PageHeader from '@/components/PageHeader'

export default function MapvetoPage() {
  const { uuid } = useParams()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)

  const laad = useCallback(async () => {
    try {
      const r = await mapvetoAPI.get(uuid)
      setData(r.data)
      setErr(null)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    }
  }, [uuid])
  useEffect(() => { laad() }, [laad])

  // Socket-sync — luister naar bans van de andere kapitein.
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) return
    const s = socketIO({ auth: { token }, transports: ['websocket', 'polling'], reconnection: true })
    const sub = () => s.emit('mapveto:subscribe', uuid)
    s.on('connect', sub)
    s.on('reconnect', sub)
    s.on('mapveto:updated', () => laad())
    return () => { s.emit('mapveto:unsubscribe', uuid); s.disconnect() }
  }, [uuid, laad])

  const banMap = async (map) => {
    setBusy(true)
    try { await mapvetoAPI.ban(uuid, map); await laad() }
    catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  const loadWinner = async () => {
    if (!window.confirm(`Server naar "${data.session.winner_map}" laden?`)) return
    setBusy(true)
    try {
      await mapvetoAPI.load(uuid)
      setErr(null)
      alert(`changelevel ${data.session.winner_map} verstuurd`)
    } catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  const toggleCheckin = async () => {
    setBusy(true)
    try { await mapvetoAPI.checkin(uuid); await laad() }
    catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => alert('Link gekopieerd — deel met de andere kapitein'))
      .catch(() => {})
  }

  if (err && !data) return (
    <div className="max-w-2xl mx-auto p-6">
      <Card><CardContent className="p-6 text-signal">{err}</CardContent></Card>
    </div>
  )
  if (!data) return (
    <div className="max-w-2xl mx-auto p-6 flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Laden…
    </div>
  )

  const { session, role } = data
  const isMyTurn = (role === 'a' && session.current_turn === 'a')
                || (role === 'b' && session.current_turn === 'b')
                || role === 'admin'
  const isDone = session.current_turn === 'done'
  const beurtNaam = session.current_turn === 'a' ? session.team_a_name
                  : session.current_turn === 'b' ? session.team_b_name
                  : '—'

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <PageHeader
        icon={Trophy}
        title="Map-veto"
        subtitle={`${session.team_a_name} vs ${session.team_b_name}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyLink}>
              <Copy className="h-4 w-4 mr-1" />Deel link
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/regie/servers"><ArrowLeft className="h-4 w-4 mr-1" />Terug</Link>
            </Button>
          </div>
        }
      />

      {/* Beurt-indicator */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">Jij bent</div>
            <Badge variant="outline" className="text-xs">
              {role === 'a' ? `${data.team_role === 'captain' ? 'kapitein' : 'lid'} ${session.team_a_name}`
                : role === 'b' ? `${data.team_role === 'captain' ? 'kapitein' : 'lid'} ${session.team_b_name}`
                : role === 'admin' ? 'admin (override mogelijk)'
                : 'spectator'}
            </Badge>
          </div>
          <div className="text-sm">
            {isDone ? (
              <span className="text-live font-semibold flex items-center gap-1">
                <Trophy className="h-4 w-4" /> Winnaar: {session.winner_map}
              </span>
            ) : (
              <span>Beurt: <span className={`font-bold ${isMyTurn ? 'text-acid' : 'text-muted-foreground'}`}>{beurtNaam}</span></span>
            )}
          </div>
        </CardContent>
      </Card>

      {err && (
        <div className="text-sm text-signal px-1">{err}</div>
      )}

      {/* Map-pool */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {session.map_pool.map(map => (
          <button key={map} type="button"
                  disabled={!isMyTurn || busy || isDone}
                  onClick={() => banMap(map)}
                  className={`relative rounded-md border p-4 text-center transition-all ${
                    isMyTurn && !isDone
                      ? 'border-acid/40 bg-acid/5 hover:border-signal hover:bg-signal/10 cursor-pointer hover:scale-[1.02]'
                      : 'border-border/40 bg-card/40 opacity-60 cursor-not-allowed'
                  } ${isDone && map === session.winner_map ? '!border-live !bg-live/10' : ''}`}>
            {isDone && map === session.winner_map && (
              <Trophy className="h-4 w-4 text-live absolute top-1 right-1" />
            )}
            <div className="font-mono text-sm font-semibold">{map}</div>
            {isMyTurn && !isDone && <div className="text-[10px] text-signal mt-1">klik om te bannen</div>}
          </button>
        ))}
      </div>

      {/* Geband */}
      {session.bans.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Geband ({session.bans.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {session.bans.map((b, i) => (
                <div key={i} className="inline-flex items-center gap-1 rounded border border-signal/30 bg-signal/5 px-2 py-1 text-xs">
                  <X className="h-3 w-3 text-signal" />
                  <span className="font-mono">{b.map}</span>
                  <span className="text-[9px] text-muted-foreground">
                    ({b.team === 'a' ? session.team_a_name : session.team_b_name})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Load-knop bij winnaar (admin only, alleen als server_identifier geset is) */}
      {isDone && session.winner_map && session.server_identifier && role === 'admin' && (
        <Card className="border-live/40 bg-live/5">
          <CardContent className="p-4 flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold text-live">Klaar om te laden</div>
              <div className="text-xs text-muted-foreground">
                Stuurt <code>changelevel {session.winner_map}</code> naar server
              </div>
            </div>
            <Button onClick={loadWinner} disabled={busy}>
              <Play className="h-4 w-4 mr-1" /> Load op server
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
